import { supabase } from '../lib/supabase';

export const uploadHistoryService = {
  // Get all upload history for current user
  async getAll(filters = {}) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    let query = supabase?.from('upload_history')?.select('*, bucket_configurations(bucket_name, bucket_region)')?.eq('user_id', user?.id);

    // Apply filters
    if (filters?.status) {
      query = query?.eq('upload_status', filters?.status);
    }
    if (filters?.bucketId) {
      query = query?.eq('bucket_id', filters?.bucketId);
    }
    if (filters?.startDate) {
      query = query?.gte('created_at', filters?.startDate);
    }
    if (filters?.endDate) {
      query = query?.lte('created_at', filters?.endDate);
    }

    query = query?.order('created_at', { ascending: false });

    const { data, error } = await query;

    if (error) throw error;

    // Convert snake_case to camelCase
    return data?.map(upload => ({
      id: upload?.id,
      userId: upload?.user_id,
      bucketId: upload?.bucket_id,
      fileName: upload?.file_name,
      fileSize: upload?.file_size,
      fileType: upload?.file_type,
      filePath: upload?.file_path,
      uploadStatus: upload?.upload_status,
      uploadStartedAt: upload?.upload_started_at,
      uploadCompletedAt: upload?.upload_completed_at,
      errorMessage: upload?.error_message,
      createdAt: upload?.created_at,
      bucketInfo: upload?.bucket_configurations ? {
        bucketName: upload?.bucket_configurations?.bucket_name,
        bucketRegion: upload?.bucket_configurations?.bucket_region
      } : null
    })) || [];
  },

  // Create upload history entry
  async create(uploadData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('upload_history')?.insert({
        user_id: user?.id,
        bucket_id: uploadData?.bucketId,
        file_name: uploadData?.fileName,
        file_size: uploadData?.fileSize,
        file_type: uploadData?.fileType,
        file_path: uploadData?.filePath,
        upload_status: uploadData?.uploadStatus || 'pending'
      })?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      userId: data?.user_id,
      bucketId: data?.bucket_id,
      fileName: data?.file_name,
      fileSize: data?.file_size,
      fileType: data?.file_type,
      filePath: data?.file_path,
      uploadStatus: data?.upload_status,
      uploadStartedAt: data?.upload_started_at,
      createdAt: data?.created_at
    };
  },

  // Update upload status
  async updateStatus(uploadId, status, errorMessage = null) {
    const updateData = {
      upload_status: status
    };

    if (status === 'completed') {
      updateData.upload_completed_at = new Date()?.toISOString();
    }

    if (errorMessage) {
      updateData.error_message = errorMessage;
    }

    const { data, error } = await supabase?.from('upload_history')?.update(updateData)?.eq('id', uploadId)?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      uploadStatus: data?.upload_status,
      uploadCompletedAt: data?.upload_completed_at,
      errorMessage: data?.error_message
    };
  },

  // Delete upload history entry
  async delete(uploadId) {
    const { error } = await supabase?.from('upload_history')?.delete()?.eq('id', uploadId);

    if (error) throw error;
    return { success: true };
  },

  // Get upload statistics
  async getStats() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('upload_history')?.select('upload_status, file_size')?.eq('user_id', user?.id);

    if (error) throw error;

    const stats = {
      totalUploads: data?.length,
      completed: data?.filter(u => u?.upload_status === 'completed')?.length,
      failed: data?.filter(u => u?.upload_status === 'failed')?.length,
      pending: data?.filter(u => u?.upload_status === 'pending')?.length,
      totalSize: data?.reduce((sum, u) => sum + (u?.file_size || 0), 0)
    };

    return stats;
  },

  // Subscribe to real-time changes for upload history
  subscribeToChanges(userId, callback) {
    const channel = supabase?.channel('upload_history_changes')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'upload_history',
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          // Convert snake_case to camelCase for consistency
          const convertedPayload = {
            eventType: payload?.eventType,
            new: payload?.new ? {
              id: payload?.new?.id,
              userId: payload?.new?.user_id,
              bucketId: payload?.new?.bucket_id,
              fileName: payload?.new?.file_name,
              fileSize: payload?.new?.file_size,
              fileType: payload?.new?.file_type,
              filePath: payload?.new?.file_path,
              uploadStatus: payload?.new?.upload_status,
              uploadStartedAt: payload?.new?.upload_started_at,
              uploadCompletedAt: payload?.new?.upload_completed_at,
              errorMessage: payload?.new?.error_message,
              createdAt: payload?.new?.created_at
            } : null,
            old: payload?.old ? {
              id: payload?.old?.id
            } : null
          };
          callback(convertedPayload);
        }
      )?.subscribe();

    return channel;
  },

  // Unsubscribe from real-time changes
  unsubscribeFromChanges(channel) {
    if (channel) {
      supabase?.removeChannel(channel);
    }
  }
};